import { FieldWrapper } from "@progress/kendo-react-form";
import { Input, InputProps } from "@progress/kendo-react-inputs";
import { Error, Hint } from "@progress/kendo-react-labels";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Eye, EyeOff } from "lucide-react";
import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { If, Then } from "react-if";
import CopyToClipboard from "@/components/CopyToClipboard";
import { renderTooltipText } from "@/components/Kendo/ToolTip/renderTooltipText";
import FormLabel from "./FormLabel";
import IconHint from "./IconHint";
import { useControlledField, type FieldValidator } from "./useControlledField";
import "./style.scss";

export interface FormInputProps
  extends Omit<InputProps, "name" | "value" | "onChange" | "valid"> {
  name: string;
  validator?: FieldValidator;
  label?: string;
  hint?: string;
  hintIcon?: boolean;
  optional?: boolean;
  /** Show error before user blurs the field. Defaults to false. */
  showErrorOnPristine?: boolean;
  /** Enable copy-to-clipboard icon at the right edge */
  copyToText?: boolean;
  /** For type="password" fields: show an eye icon to toggle masked/plain text */
  revealable?: boolean;
  floatingLabel?: boolean;
  floatLabel?: string;
}

export const FormInput: React.FC<FormInputProps> = ({
  name,
  id,
  autoComplete = "off",
  validator,
  label,
  hint,
  hintIcon = false,
  optional,
  showErrorOnPristine = false,
  copyToText,
  revealable,
  floatingLabel,
  floatLabel,
  type,
  disabled,
  placeholder,
  onBlur: consumerOnBlur,
  onKeyDown: consumerOnKeyDown,
  ariaDescribedBy: consumerDescribedBy,
  ...inputProps
}) => {
  const { t } = useTranslation();
  const { field, fieldState } = useControlledField({ name, validator });
  const [isRevealed, setIsRevealed] = useState(false);
  // Gate on the declared type: `revealable` must never turn a text field into
  // a masked password box (field defs pass the flag through generically).
  const isRevealable = revealable && type === "password";
  const resolvedType = isRevealable && isRevealed ? "text" : type;
  const showError =
    !!fieldState.error && (showErrorOnPristine || fieldState.isTouched);
  const showHint = !!hint;
  const showHintText = showHint && !hintIcon;
  const showHintIcon = showHint && hintIcon;
  const inputId = id || name;
  const labelId = label ? `${inputId}_label` : "";
  const hintId = showHint ? `${inputId}_hint` : "";
  const errorId = showError ? `${inputId}_error` : "";

  return (
    <FieldWrapper>
      <Tooltip
        anchorElement="target"
        parentTitle={true}
        style={{ maxWidth: 400 }}
        showCallout={false}
        content={renderTooltipText}
      >
        <FormLabel
          id={labelId}
          editorId={inputId}
          editorValid={!fieldState.error}
          editorDisabled={!!disabled}
          floating={floatingLabel}
          floatLabel={floatLabel}
          label={label}
          value={
            field.value != null && String(field.value).length > 0
              ? String(field.value)
              : undefined
          }
          placeholder={placeholder}
          optional={optional}
        >
          <div className={`k-form-field-wrap ${inputId}`}>
            <Input
              {...inputProps}
              id={inputId}
              name={name}
              type={resolvedType}
              disabled={disabled}
              placeholder={placeholder}
              autoComplete={autoComplete}
              ariaDescribedBy={
                [hintId, errorId, consumerDescribedBy]
                  .filter(Boolean)
                  .join(" ") || undefined
              }
              aria-invalid={!!fieldState.error || undefined}
              style={{
                paddingRight:
                  20 + (copyToText ? 24 : 0) + (isRevealable ? 24 : 0),
              }}
              valid={!fieldState.error}
              value={(field.value as string | number | undefined) ?? ""}
              onChange={e => field.onChange(e.value)}
              onBlur={e => {
                consumerOnBlur?.(e);
                field.onBlur();
              }}
              onKeyDown={e => {
                consumerOnKeyDown?.(e);
                if (e.key === "Enter" && !e.defaultPrevented) {
                  e.preventDefault();
                }
              }}
            />
            {showHintText && <Hint id={hintId}>{hint}</Hint>}
            {showHintIcon && <IconHint hint={hint} />}
            {isRevealable && (
              <span
                role="button"
                tabIndex={0}
                aria-label={t("common.togglePasswordVisibility")}
                aria-pressed={isRevealed}
                // Scoped by field name: several revealable credential fields
                // render side by side, so a shared id would be ambiguous.
                data-test-id={`${name}-reveal-toggle`}
                onClick={() => setIsRevealed(v => !v)}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setIsRevealed(v => !v);
                  }
                }}
                style={{
                  position: "absolute",
                  // One 19px slot (14px icon + 5px gap) to the left of whatever
                  // trailing icons it shares the field with, starting from the
                  // same 15px inset the caret and copy icon use. Left on its own
                  // geometry it overlapped the copy icon and sat ~5px higher.
                  right: `${0.9375 + (showHintIcon ? 1.1875 : 0) + (copyToText ? 1.1875 : 0)}rem`,
                  // Half the 46px control — see the copy icon below.
                  top: "1.4375rem",
                  transform: "translateY(-50%)",
                  width: "0.875rem",
                  height: "0.875rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--tlx-icon-link)",
                  cursor: "pointer",
                }}
              >
                {/* 14px to match the info and copy glyphs it lines up with. */}
                {isRevealed ? (
                  <EyeOff size={14} strokeWidth={2} />
                ) : (
                  <Eye size={14} strokeWidth={2} />
                )}
              </span>
            )}
            <If condition={copyToText}>
              <Then>
                <span
                  role="button"
                  style={{
                    position: "absolute",
                    // The info icon sits at right:10 + marginRight:5 and is 14px
                    // wide, so its left edge is 29px from the field's edge. At
                    // the previous offset of 30 the two icons all but touched
                    // (they overlapped at the old 20px icon size). 34 leaves a
                    // clean 5px gap between them (QA-28).
                    // 15px from the trailing edge when it stands alone (matching the
                    // field's own inset); 34px when it must clear the info icon.
                    right: showHintIcon ? "2.125rem" : "0.9375rem",
                    // Half the 46px control, NOT 50% — `.k-form-field-wrap` also holds
                    // <Hint> and <Error>, so a percentage drops this icon below the
                    // input once a validation error renders. Matches IconHint.
                    top: "1.4375rem",
                    transform: "translateY(-50%)",
                    // Box matches the 14px icon exactly (and the info icon's
                    // box), so the measured 5px gap is also the *visual* gap —
                    // a 20px box around a 14px glyph made them look adjacent.
                    width: "0.875rem",
                    height: "0.875rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--tlx-icon-link)",
                  }}
                >
                  <CopyToClipboard copyToText={field.value as string} />
                </span>
              </Then>
            </If>
            {showError && (
              <Error id={errorId}>{fieldState.error?.message}</Error>
            )}
          </div>
        </FormLabel>
      </Tooltip>
    </FieldWrapper>
  );
};

export default FormInput;
